import {VetVisitType, VisitType} from './vet-clinic-data.model';

/**
 * Główny model konfiguracji i tożsamości kliniki weterynaryjnej.
 * Steruje zachowaniem placówki w systemie: widocznością, płatnościami, regułami
 * kalendarza, zaawansowaną synchronizacją z KlinikaXP oraz powiadomieniami.
 */
export class VetClinic {

  /** Globalnie unikalny identyfikator kliniki w bazie danych. */
  public uuid: string;
  /** Wewnętrzny, numeryczny kod ID kliniki. */
  public vetcodeId: number;
  /** Identyfikator nadrzędnej spółki / sieci weterynaryjnej (Klient B2B). */
  public vClientUUID: string;
  public vclientId: number;
  /** Identyfikator konta partnerskiego. */
  public partnerVClientId: number;
  /** Status konta Vetineo (np. "ACTIVE", "SUSPENDED"). */
  public vetineoAccountStatus: string;
  /** Data rozpoczęcia współpracy / uruchomienia produkcyjnego placówki w Vetineo. */
  public vetineoStartDate: string;
  /** Data wygaśnięcia głównego abonamentu na usługi platformy Vetineo. */
  public expiries: string;
  /** Flaga ukrywająca klinikę z publicznych list/wyszukiwarek. */
  public hideFromList: boolean;
  /** Czy klinika ma być widoczna na mapie lecznic. */
  public isAvailableOnMap: boolean;
  /** Tryb offline - klinika jest wyłączona z obsługi rezerwacji online. */
  public isOffline: boolean;
  public pragmaticBoxCode: string;


  /** Oficjalna nazwa firmy/kliniki. */
  public clinicName: string;
  /** Nazwa wyświetlana pacjentom. */
  public displayName: string;
  public city: string;
  public cityName: string;
  public cityRegion: string;
  public clinicAddress1: string;
  /** Dodatkowy adres. */
  public clinicAddress2: string;
  public clinicMail: string;
  public clinicPhone: string;
  /** Opis placówki wyświetlany w profilu. */
  public about: string;

  /** Flaga: czy lecznica przyjmuje wszystkie standardowe gatunki. */
  public allPetSpecies: boolean;
  /** Zserializowana lista (klucze) specyficznych gatunków, jeśli klinika się w nich specjalizuje. */
  public servedPetSpeciesKeys: string;


  /** Główny obrazek (Cover Photo) profilu kliniki. */
  public homeImage: VImage;
  public homeImageUUID: string;
  /** Galeria zdjęć wnętrza, sprzętu i personelu. */
  public gallery: VImage[] | VetClinicPhotoGalleryImage[];
  /** Domyślny obrazek używany, gdy klinika wysyła wiadomości systemowe bez własnej grafiki. */
  public vMsgDefaultImg: VImage;
  public vMsgDefaultImgUUID: string;
  public vImagesToSetAsActive: string[];

  /** Lista lekarzy weterynarii przypisanych do tej placówki. */
  public team: VetDoctor[];
  /** Opis usług w formacie tekstowym. */
  public services: string;
  /** Ustrukturyzowane (JSON) drzewo świadczonych usług. */
  public servicesJson: string;
  /** Tablice typów wizyt (słowniki usług, np. "Szczepienie", "Konsultacja kardiologiczna"). */
  public visitTypes: VisitType[];
  public vetVisitTypes: VetVisitType[];
  public vetVisitTypeJsonArray: string;
  /** Flaga określająca, czy lecznica korzysta z nowych typów wizyt Vetineo. */
  public useVetineoVetVisitTypes: boolean;
  /** Zezwala na nadpisywanie domyślnych pakietów usług. */
  public useBundleCustomOverride: boolean;
  public bundlesOverride: Date;
  public bundlesOverrideByUserUUID: string;

  /** Rozmiar pojedynczego bloku w kalendarzu. */
  public reservationCalendarDaySize: number;
  /** Minimalny czas, z jakim pacjent musi zarezerwować standardową wizytę przed jej startem. */
  public delayReservationFromClinic: number;
  /** Minimalny czas dla wizyt przedpłacanych. */
  public delayPrepaidReservationFromClinic: number;
  /** Globalne blokady godzinowe – nie można rezerwować wizyt przed tą godziną (np. przed otwarciem). */
  public blockReservationsBeforeHour: number;
  /** Globalna blokada rezerwacji po danej godzinie. */
  public blockReservationsAfterHour: number;
  /** Blokuje możliwość zarezerwowania wizyty całkowicie poza zdefiniowanymi godzinami pracy lekarza. */
  public blockReservationsNotInWorkHours: boolean;

  /** Flaga pozwalająca wyświetlać wszystkie luki w grafiku zamiast wybranych propozycji. */
  public showAllFreeTerms: boolean;
  /** Zezwala na stosowanie spersonalizowanych grafików, nadpisujących reguły domyślne. */
  public useCustomSchedule: boolean;
  public useBookingHoursForCheckHoursList: boolean;

  /** Polityka zwrotów: Jaki procent przedpłaty klinika zwraca po przekroczeniu czasu na darmową anulację. */
  public percentageRefundValueAfterExpiration: number;
  /** Procent z kwoty vouchera do potrącenia przy standardowych zwrotach. */
  public reservationCancellationRefund: number;
  /** Dedykowany adres URL, na który pacjent jest przekierowywany po anulacji wizyty. */
  public cancellationPageUrl: string;
  /** Czy wysyłać do fakturę dla osób prywatnych do KSeF? */
  public sendInvoiceForPersonToKSeF: boolean;

  /** Po ilu minutach przesyłać wizytę do użytkownika? */
  public visitSyncDelayMinutes: number;
  /** Czy nie wysyłać wiadomości email o braku synchronizacji z KlinikaXP? */
  public disableCheckKlinikaXpSync: boolean;
  /** Pozwala na uruchamianie skryptów synchronizujących nawet w dni ustawowo wolne od pracy. */
  public holidaysCheckSyncAllowed: boolean;
  /** Flaga ułatwiająca mapowanie: czas trwania wizyty w Vetineo może być inny niż domyślny czas w systemie KlinikiXP. */
  public useDifferentVisitTypeTimeInKlinikaXp: boolean;

  /** Blokada chroniąca przed pobraniem starych historii leczenia od nowych klinik. */
  public isOldRecommendationsSyncBlocked: boolean;
  public isOldLabTestDataBlocked: boolean;
  /** Data, od której system Vetineo zaczął synchronizować historię dla tej konkretnej kliniki. */
  public dateSinceSyncVisitStarted: string;

  public visitUpdateSyncPushBlock: boolean;
  public visitUpdateSyncPushBlockAfter: boolean;
  public visitUpdateSyncPushBlockValue: number;
  public visitUpdateSyncPushBlockValueAfter: number;
  /** Tryb synchronizacji dobowej. */
  public visitSyncHourMode: boolean;
  public visitSyncHour: string;


  /** Główny włącznik bramki płatności dla pacjentów. */
  public paymentsEnabled: boolean;
  /** Flaga integracji z Przelewy24 (Merchant). */
  public isMerchantActive: boolean;
  /** Identyfikator kliniki w systemie Przelewy24. */
  public merchantId: number;
  public p24RegistrationDataUUID: string;

  /** Adres e-mail dedykowany do odbierania faktur rozliczeniowych z platformy. */
  public invoiceOfficeEmail: string;
  /** Integracja z programem Fakturownia - przechowuje identyfikator autoryzacyjny utworzonego tam konta. */
  public fakturowniaCreateAccountResponseUUID: string;
  /** Czy oznaczać w serwisie Fakturownia faktury wystawione dla klientów indywidulanych jako wystawione do paragonu? */
  public markInvoicesIssuedToReceipt: boolean;
  /** Osobny adres email dedykowany do informowania o utworzeniu zwrotu dla klienta */
  public refundInfoEmail: string;

  /** Ile dni wstecz wyświetlać terminy wizyt podczas przygotowywania linku do płatności? */
  public paymentLinkAppointmentsQueryDays: number;
  /** Instrukcja dołączana do sprzedaży voucherów. */
  public voucherSellInfo: string;
  public voucherInfoFirstInSubject: boolean;

  /** Główny przełącznik modułu wiadomości wychodzących. */
  public messagesEnabled: boolean;
  /** Czy klinika chce otrzymywać alerty o braku synchronizacji? */
  public sendTechnicalAlerts: boolean;
  public technicalAlertsEmail: string;
  /** Włączone przesyłanie do lecznicy szczegółowych informacji o rezerwacji */
  public sendExtendedNotifications: boolean;
  /** Wysyłać powiadomienia po rejestracji na wizytę jako "potwierdzone" */
  public sendConfirmNotification: boolean;
  /** Włączone przesyłanie mailem dodatkowych informacji o typie wizyty */
  public sendVisitTypeAdditionalInfo: boolean;
  /** Włączone przesyłanie mailem na ile godzin maksymalnie przed terminem można go odwołać */
  public addCancellationHoursToMail: boolean;
  /** Tekst zachącający do instalowania aplikacji (do maili po rezerwacji i potwierdzeniu wizyty). */
  public vetineoPromotionEmailAddon: string;
  public disablePushForUpdateVisit: boolean;

  /** WŁASNA BRAMKA SMS: Klinika może podpiąć własne konto (np. SMSAPI/SerwerSMS). */
  public enableSMSSerwer: boolean;
  public smsSerwerUser: string;
  public smsSerwerPwd: string;
  public smsSerwerSender: string;
  public smsProvider: string;
  public smsApiKey: string;
  /** Data wygaśnięcia modułu SMS lecznicy. */
  public smsModuleExpiration: string;
  /** Czy wysyłać maila do kliniki o wygaśnięciu modułu sms? */
  public sendEmailAboutCrmSmsModuleExpiration: boolean;

  /** Czas do przesłania przypomnienia o rezerwacji (w godzinach). */
  public hoursToSendReminder: number;
  /** UUID powiązanego profilu/szablonu ustawień dla przypomnień. */
  public nextVisitReminderSettingsUUID: string;
  public nextVisitReminderType: string;
  /** Na ile tygodni przed wizytą umożliwiać rezerwację terminu dla wizyt z przypomnienia. */
  public daysToMakeReservationTermAvailableForReminders: number;

  /** Flaga aktywująca obsługę abonamentowych Planów Abonamentowych (Care Plans). */
  public carePlanEnabled: boolean;
  /** Indywidualna treść wyświetlana w aplikacji przed potwierdzeniem wyników badań. */
  public labTestDataDuringPreparationText: string;
  /** Włączony tryb potwierdzania wyników badań w Dashboardzie.
   */
  public delayLabTestDataAcceptationSync: boolean;
  /** Po ilu godzinach od utworzenia wizyty potwierdzić automatycznie wyniki badań? */
  public labTestDataAcceptationSyncDelayInHours: number;

  /** Bufor (Cache) liczby pacjentów - ułatwia szybkie wyświetlanie statystyk w panelu bez liczenia rekordów. */
  public usersCounter: number;
  public usersSyncCounter: number;

  /** Włączony tryb blokowania porannych godzin (poza godzinami pracy) */
  public vrAutoLockMode: number;
  /** Do jakiej godziny ma działać automatyczna blokada rezerwacji porannych? */
  public vrAutoLockEndHour: string;
  /** Aktualna wersja regulaminu / RODO, którą zaakceptował zarząd kliniki. */
  public legalVer: string;

}

/**
 * Model reprezentujący profil Lekarza Weterynarii.
 * Zawiera dane wizytówkowe, reguły przyjmowania płatności (przedpłaty) oraz
 * zaawansowany silnik reguł kalendarzowych (blokady, rezerwacje progresywne).
 */
export class VetDoctor {


  /** Globalnie unikalny identyfikator lekarza. */
  public uuid: string;

  /** Pełne imię i nazwisko (wraz z tytułem naukowym/zawodowym, np. "lek. wet. Jan Kowalski"). */
  public name: string;

  /** Skrócona nazwa do wyświetlania w ciasnych widokach kalendarza. */
  public shortName: string;

  /** Opis specjalizacji i doświadczenia (Bio).
   */
  public description: string;

  /** Główny numeryczny identyfikator lekarza (Work Hour Object ID).
   * Funkcjonalnie pełni rolę `doctorId`. Wykorzystywany przez metody pomocnicze
   * (np. getVetDoctor) do precyzyjnego wyszukiwania i wyciągania obiektu
   * konkretnego specjalisty z tablicy zespołu (team) danej kliniki.
   */
  public whObjId: number;

  /** Pozycja na liście (Sort Order)
   */
  public orderIndex: number;

  /** Miniaturka zdjęcia profilowego na listy i do kalendarza. */
  public photo: VImage;
  public photoImageUUID: string;

  /** Pełnowymiarowe zdjęcie wyświetlane na dedykowanej karcie profilowej lekarza. */
  public largePhoto: VImage;
  public largePhotoImageUUID: string;


  /** Flaga wymuszająca przedpłatę.
   */
  public prepaidRequired: boolean;

  /** Kwota przedpłaty. */
  public prepaidVisitPrice: number;

  /** Główny włącznik (Master Switch) - pozwala całkowicie ukryć grafiki tego lekarza przed rejestracją online. */
  public onlineReservationDisabled: boolean;

  /** Standardowy, twardy czas trwania wizyty liczony w minutach. */
  public stdVisitMinutes: number;

  /** Domyślny czas trwania wizyty dla tego konkretnego lekarza (nadpisuje wartości globalne kliniki). */
  public defaultVisitMinutes: number;

  /** Czas na anulowanie terminu z voucherem w przypadku zabiegu medycznego.
   */
  public medicalProcedureCancellationTime: number;

  /** Flaga techniczna obsługująca wizyty powiązane. */
  public hasLinkedReservation: boolean;

  /** Główny włącznik trybu wizyt progresywnych dla tego specjalisty. */
  public progressiveReservations: boolean;

  /** Maksymalna przerwa (do rejestracji) */
  public maxHoleSize: number;

  /** Dostępne terminy (wcześniejsze). */
  public lowerReservationsLimit: number;

  /** Dostępne terminy (na koniec dnia). */
  public upperReservationsLimit: number;

  /** JSON: Całodniowe reguły dni tygodnia rezerwacji progresywnych. */
  public progressiveReservationsDaysJson: string;

  /** JSON: Zaawansowane okna godzinowe rezerwacji progresywnych. */
  public progressiveReservationsDayHoursJson: string;

  /** JSON: Wersja okien godzinowych zawierająca flagę isFullDay (na potrzeby renderowania interfejsu w Dashboardzie). */
  public progressiveReservationsDayHoursJsonDashboard: string;


  /** JSON: Dni, w których ten lekarz nie przyjmuje pacjentów z rezerwacji online – wizytę można umówić TYLKO telefonicznie. */
  public phoneOnlyReservationDaysJson: string;

  /** JSON: Dni tygodnia, w których lekarz przyjmuje tylko pacjentów stacjonarnie, bez planowanych rezerwacji. */
  public stationaryReservationDaysJson: string;

  /** JSON: Sztywne, całodniowe blokady kalendarza. */
  public lockedReservationDaysJson: string;

  /** JSON: Zablokowane powtarzalne okna godzinowe w bazie. */
  public lockedReservationDayHoursJson: string;

  /** JSON: Wersja zablokowanych okien godzinowych dla UI w Dashboardzie. */
  public lockedReservationDayHoursJsonDashboard: string;

  /** JSON: Reguły nakładane na kalendarz poza standardowym cyklem tygodniowym. */
  public advancedLockedDayHoursJson: string;

  /** Flaga włączająca specjalne traktowanie godzin nocnych dla tego lekarza. */
  public nightShiftLock: boolean;

  /** Godzina rozpoczęcia dyżuru nocnego. Wtedy mogą obowiązywać inne zasady. */
  public nightShiftLockHour: string;

  /** JSON: Przypisanie dyżurów nocnych do konkretnych dni tygodnia. */
  public nightShiftLockHourDaysJsonDashboard: string;

}

/**
 * Główny model reprezentujący metadane obrazu.
 * Nie zawiera samych bajtów pliku, a jedynie informacje pozwalające na jego
 * zlokalizowanie, zaserwowanie odpowiedniej rozdzielczości i kategoryzację.
 */
export class VImage {
  /** Globalnie unikalny identyfikator obrazu. */
  public uuid: string;

  /** Oryginalna nazwa pliku. */
  public name: string;

  /** Względna lub bezwzględna ścieżka do pliku.
   */
  public path: string;

  /** Typ MIME pliku (np. "image/jpeg", "image/png"). */
  public contentType: string;

  /** Zserializowana informacja o dostępnych wersjach/rozmiarach wygenerowanych przez backend.
   */
  public versions: string;

  /** Główny folder przypisany do danego modułu. */
  public imageFolder: string;

  /** Podfolder odpowiadający za konkretną, zaktualizowaną wersję obrazu. */
  public currentVersionFolder: string;

  /** Identyfikator nadrzędnego folderu strukturyzujący pliki. */
  public mFolderUUID: string;

  /** Flaga określająca, czy to konkretne zdjęcie ma być automatycznie używane w artykule. */
  public galleryIncluded: boolean;

  constructor() {}
}

/**
 * Model relacyjny, łączący klinikę weterynaryjną ze zdjęciami.
 * Reprezentuje pojedynczy wpis w publicznej galerii zdjęć placówki.
 */
export class VetClinicPhotoGalleryImage {
  /** Pozycja zdjęcia w galerii (Sort Order).
   * Pozwala recepcji samodzielnie układać kolejność zdjęć.
   */
  public orderIndex: number;

  /** Identyfikator kliniki, do której należy to zdjęcie z galerii. */
  public vetClinicUUID: string;

  /** Identyfikator fizycznego obrazu (relacja do VImage). */
  public vImageUUID: string;

  constructor(orderIndex: number, vetClinicUUID: string, vImageUUID: string) {
    this.orderIndex = orderIndex;
    this.vetClinicUUID = vetClinicUUID;
    this.vImageUUID = vImageUUID;
  }
}

/**
 * Obiekt transferu danych (DTO) reprezentujący fizyczny ładunek pliku
 * przesyłany z przeglądarki (Frontend) na serwer (Backend).
 */
export class VImageUpload {
  /** Nazwa pliku z rozszerzeniem (np. "avatar.jpg"). */
  public name: string;

  /** Fizyczna zawartość pliku.
   */
  public data: string | ArrayBuffer;

  /** Typ MIME określający format przesyłanego pliku. */
  public contentType: string;

  constructor(name: string, contentType: string, data: string | ArrayBuffer) {
    this.name = name;
    this.contentType = contentType;
    this.data = data;
  }
}

/**
 * Obiekt grupujący (Wrapper / DTO) wykorzystywany podczas edycji profilu lekarza.
 * Pozwala na przesłanie jednym żądaniem HTTP (Request) zarówno zaktualizowanych danych
 * tekstowych lekarza, jak i nowych plików graficznych (miniaturki i dużego zdjęcia).
 */
export class VetDoctorEditData {
  /** Zmodyfikowany obiekt lekarza weterynarii. */
  public vetDoctor: VetDoctor;

  /** Opcjonalny ładunek z nowym, małym zdjęciem profilowym. */
  public photoUpload: VImageUpload;

  /** Opcjonalny ładunek z nowym, dużym zdjęciem na wizytówkę. */
  public largePhotoUpload: VImageUpload;

  constructor(vetDoctor: VetDoctor, photoUpload: VImageUpload, largePhotoUpload: VImageUpload) {
    this.vetDoctor = vetDoctor;
    this.photoUpload = photoUpload;
    this.largePhotoUpload = largePhotoUpload;
  }
}
